<template>
  <app-page-container custom="100%">
    <app-spinner v-if="!is_page_loaded"/>
    <template v-else>
      <app-cells>
        <div class="date-picker-wrapper">
          <VueCtkDateTimePicker
            id="date_picker_balance"
            class="custom-date-time-picker custom-date-time-picker--range"
            v-model="date_range"
            :custom-shortcuts="custom_shortcuts"
            @is-hidden="onIsHiddenPicker"
            @input="onInputPicker"
            format="YYYY-MM-DD"
            formatted="ll"
            label="Select dates"
            color="#77b017"
            button-color="#77b017"
            overlay
            no-header
            no-label
            locale="en"
            range
            ref="rage_picker"
          />
          <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
        </div>
        <app-input
          @input.native="onListSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-filters
          @change="onChangeFilter"
          v-model="filter_confirmed"
          :filters="filter_confirmed_options"
          name="filterType"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': is_table_loading }"
      >
        <template #h__start>
          <div class="table-default__left">Start</div>
        </template>
        <template #h__finish>
          <div class="table-default__left">Finish</div>
        </template>
        <template #h__itinerary>
          <div class="table-default__left">Itinerary</div>
        </template>
        <template #h__group>
          <div class="table-default__left">Group</div>
        </template>
        <template #h__cars_required>№<br>of cars</template>
        <template #h__cars>
          <div class="table-default__left">Cars</div>
        </template>
        <template #h__drivers>
          <div class="table-default__left">Drivers</div>
        </template>
        <template #h__start_from>
          <div class="table-default__left">Start from</div>
        </template>
        <template #h__finish_at>
          <div class="table-default__left">Finish at</div>
        </template>
        <template #start="{ row }">
          <div class="table-default__left">{{ dateFormat(row.tour_start) }}</div>
        </template>
        <template #finish="{ row }">
          <div class="table-default__left">{{ dateFormat(row.tour_end) }}</div>
        </template>
        <template #pax="{ row }">{{ row.pax || '-' }}</template>
        <template #ref="{ row }">
          <router-link :to="{ name: 'reservation-safari-detail', params: { id: row.id } }"
                       class="table-default__link-border table-default__link-border--center">
            {{ row.id }}
          </router-link>
        </template>
        <template #type="{ row }">
          <div class="status-tag" :style="{ backgroundColor: row.type.color }">
            {{ row.type.name }}
          </div>
        </template>
        <template #cars_required="{ row }">
          <template v-if="row.cars_required !== row.own_cars.length + row.rental_cars.length">
            <div class="status-tag" style="background-color: #e85c20">
              {{ row.cars_required }}
            </div>
          </template>
          <template v-else>{{ row.cars_required }}</template>
        </template>
        <template #itinerary="{ row }">
          <div class="table-default__left">{{ row.short_itinerary }}</div>
        </template>
        <template #days="{ row }">{{ row.days }}</template>
        <template #group="{ row }">
          <div class="table-default__left">
            <div v-if="!row.safari_group" class="status-tag" style="background-color: #e85c20">
              Missing
            </div>
            <router-link
              v-else
              :to="{ name: 'safari-groups-detail', params: { id: row.safari_group.id } }"
              class="table-default__link-border"
            >
              {{ row.safari_group.name }}
            </router-link>
          </div>
        </template>
        <template #edit_cars="{ row }">
          <button v-if="can_edit_cars" @click="onEditCars(row)" type="button" class="tr-block__button">
            <img src="@/assets/img/tr-arrange-icon.svg" alt="" style="max-width: none;">
          </button>
        </template>
        <template #cars="{ row }">
          <div class="table-default__left">
            <div
              v-if="row.own_cars.length < 1 && row.rental_cars.length < 1"
              class="status-tag"
              style="background-color: #e85c20"
            >
              Missing
            </div>
          </div>
          <template v-if="row.own_cars.length > 0">
            <div class="table-default__left">
              <div
                v-for="car_item in row.own_cars"
                :key="`owm_car_${car_item.id}`"
                :style="{ color: !car_item.car_confirmed ? '#d7a800' : '#0a0a0a' }"
                v-tooltip="{ content: !car_item.car_confirmed ? 'Not confirmed' : '', placement: 'top-start', offset: 10 }"
              >
                {{ car_item.car.plate_number }} / Altezza
              </div>
            </div>
          </template>
          <template v-if="row.rental_cars.length > 0">
            <div class="table-default__left">
              <div
                v-for="car_item in row.rental_cars"
                :key="`rental_car_${car_item.id}`"
                :style="{ color: !car_item.car_confirmed ? '#d7a800' : '#0a0a0a' }"
                v-tooltip="{ content: !car_item.car_confirmed ? 'Not confirmed' : '', placement: 'top-start', offset: 10 }"
              >
                {{ car_item.car.plate_number }} / {{ car_item.rental_company.name }}
              </div>
            </div>
          </template>
        </template>
        <template #drivers="{ row }">
          <div class="table-default__left">
            <div
              v-if="row.own_cars.length < 1 && row.rental_cars.length < 1"
              class="status-tag"
              style="background-color: #e85c20"
            >
              Missing
            </div>
          </div>
          <template v-if="row.own_cars.length > 0">
            <div class="table-default__left">
              <div v-for="car_item in row.own_cars" :key="`owm_car_driver_${car_item.id}`">
                <template v-if="car_item.driver">
                  <span
                    :style="{ color: !car_item.driver_confirmed ? '#d7a800' : '#0a0a0a' }"
                    v-tooltip="{ content: !car_item.driver_confirmed ? 'Not confirmed' : '', placement: 'top-start', offset: 10 }"
                  >
                    {{ car_item.driver.full_name }}
                  </span>
                </template>
                <template v-else><span style="font-weight: 800; color: #e85c20;">Not assigned</span></template>
              </div>
            </div>
          </template>
          <template v-if="row.rental_cars.length > 0">
            <div class="table-default__left">
              <div v-for="car_item in row.rental_cars" :key="`rental_car_driver_${car_item.id}`">
                <template v-if="car_item.driver">
                  <span
                    :style="{ color: !car_item.driver_confirmed ? '#d7a800' : '#0a0a0a' }"
                    v-tooltip="{ content: !car_item.driver_confirmed ? 'Not confirmed' : '', placement: 'top-start', offset: 10 }"
                  >
                    {{ car_item.driver.full_name }}
                  </span>
                </template>
                <template v-else><span style="font-weight: 800; color: #e85c20;">Not assigned</span></template>
              </div>
            </div>
          </template>
        </template>
        <template #start_from="{ row }">
          <div class="table-default__left">{{ row.start_from.short_name }}</div>
        </template>
        <template #finish_at="{ row }">
          <div class="table-default__left">{{ row.finish_at.short_name }}</div>
        </template>
      </v-client-table>
      <app-cells :position="pagination.pages > 1 ? 'between' : 'end'">
        <paginate
          :clickHandler="onClickPagination"
          v-if="pagination.pages > 1"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
      <modal
        name="edit-cars"
        @before-close="beforeCarsClose"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('edit-cars')" :disabled="carsSaving || assignmentLockPending" class="popup__close" type="button"></button>
          <div class="popup__title">
            {{ dateShortFormat(edit_row.tour_start) }} - {{ dateShortFormat(edit_row.tour_end) }}
            [{{ edit_row.cars_required }}] {{ edit_row.lead_name }} ({{ edit_row.pax }})
            <div>{{ edit_row.days }} days: {{ edit_row.short_itinerary }}</div>
          </div>
          <div class="popup__body">
            <form @submit.prevent="onCheckEditCarsForm">
              <p>Unlocking takes effect immediately. New locks are applied when you save.</p>
              <div class="arrange-popup">
                <div class="arrange-popup__part">
                  <div class="arrange-popup__block-title">
                    <span>Own cars</span>
                    <button @click="onAddOwnCar" type="button">
                      <img src="@/assets/img/add-big-button-icon.svg" alt="">
                    </button>
                  </div>
                  <div class="transfer-sortable transfer-sortable--own transfer-sortable--full">
                    <div v-for="(element, index) in own_cars" :key="`own_item_${index}`"
                         class="transfer-sortable__item">
                      <div class="transfer-sortable__row">
                        <app-form-field required>
                          <app-select
                            @input="onOwnCarSelected(element.car, index)"
                            v-model="element.car"
                            :options="cars_options"
                            :clearable="false"
                            :get-option-label="item => item.plate_number"
                            :display-field="option => option.plate_number"
                            placeholder="Select car"
                            :readonly="element.car_assignment_locked || carsSaving || assignmentLockPending"
                            :error="$v.own_cars.$each[index].car.$error"
                          />
                          <template #error>
                            <div v-if="$v.own_cars.$each[index].car.$dirty && !$v.own_cars.$each[index].car.required">
                              Required field
                            </div>
                          </template>
                        </app-form-field>
                        <app-form-field>
                          <app-select
                            v-model="element.driver"
                            :options="drivers_options"
                            :clearable="true"
                            label="full_name"
                            placeholder="Enter driver"
                            :readonly="element.guide_assignment_locked || carsSaving || assignmentLockPending"
                          />
                        </app-form-field>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Car confirmed:
                          <app-switcher v-model="element.car_confirmed" :readonly="!element.car"/>
                        </app-cells>
                        <app-cells :indent="false">
                          Driver confirmed:
                          <app-switcher v-model="element.driver_confirmed" :readonly="!element.driver"/>
                        </app-cells>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Primary car:
                          <app-switcher
                            @change.native="onPrimarySwitcherChanged($event, 'own_cars', index)"
                            v-model="element.primary_car"
                            :readonly="!element.car"
                          />
                        </app-cells>
                        <app-cells :indent="false">
                          Lock car:
                          <SafariAssignmentLock
                            :assignment="element" :safari-id="edit_row.id"
                            field="car_assignment_locked" :disabled="carsSaving || assignmentLockPending"
                            @busy="assignmentLockPending = $event"
                          />
                        </app-cells>
                        <app-cells :indent="false">
                          Lock guide:
                          <SafariAssignmentLock
                            :assignment="element" :safari-id="edit_row.id"
                            field="guide_assignment_locked" :disabled="carsSaving || assignmentLockPending"
                            @busy="assignmentLockPending = $event"
                          />
                        </app-cells>
                      </div>
                      <button
                        @click="onRemoveOwnItem(index)"
                        class="transfer-sortable__close"
                        type="button"
                        :disabled="
                          carsSaving || assignmentLockPending ||
                          element.car_assignment_locked ||
                          element.guide_assignment_locked ||
                          element._initial_car_assignment_locked ||
                          element._initial_guide_assignment_locked
                        "
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="">
                      </button>
                    </div>
                  </div>
                </div>
                <div class="arrange-popup__part">
                  <div class="arrange-popup__block-title">
                    <span>Rental</span>
                    <button @click="onAddRental" type="button">
                      <img src="@/assets/img/add-big-button-icon.svg" alt="">
                    </button>
                  </div>
                  <div class="transfer-sortable transfer-sortable--own transfer-sortable--full">
                    <div v-for="(element, index) in rental_cars" :key="`rental_item_${index}`"
                         class="transfer-sortable__item">
                      <div class="transfer-sortable__row">
                        <app-form-field>
                          <app-select
                            @input="onRentalCompanySelected(index)"
                            v-model="element.rental_company"
                            :options="company_options"
                            :clearable="false"
                            placeholder="Select company"
                            :error="$v.rental_cars.$each[index].rental_company.$error"
                          />
                          <template #error>
                            <div
                              v-if="$v.rental_cars.$each[index].rental_company.$dirty && !$v.rental_cars.$each[index].rental_company.required">
                              Required field
                            </div>
                          </template>
                        </app-form-field>
                        <div class="arrange-popup__cost">
                          <span>Price (USD):</span>
                          <app-form-field>
                            <app-input
                              v-model.number="element.price"
                              type="number"
                              placeholder="0"
                              id="cost"
                              name="cost"
                              class="input--100"
                              :error="$v.rental_cars.$each[index].price.$error"
                            />
                            <template #error>
                              <div
                                v-if="$v.rental_cars.$each[index].price.$dirty && !$v.rental_cars.$each[index].price.required">
                                Required field
                              </div>
                            </template>
                          </app-form-field>
                        </div>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-form-field>
                          <app-select
                            @input="onRentalCarSelected(element.car, index)"
                            v-model="element.car"
                            :options="element.rental_company?.rental_cars"
                            :clearable="false"
                            label="plate_number"
                            placeholder="Select car"
                            :readonly="!element.rental_company"
                          />
                        </app-form-field>
                        <app-form-field>
                          <app-select
                            v-model="element.driver"
                            :options="element.rental_company?.drivers"
                            :clearable="true"
                            label="full_name"
                            placeholder="Select driver"
                            :readonly="!element.car"
                          />
                        </app-form-field>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Car confirmed:
                          <app-switcher v-model="element.car_confirmed" :readonly="!element.car"/>
                        </app-cells>
                        <app-cells :indent="false">
                          Driver confirmed:
                          <app-switcher v-model="element.driver_confirmed" :readonly="!element.driver"/>
                        </app-cells>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Primary car:
                          <app-switcher
                            @change.native="onPrimarySwitcherChanged($event, 'rental_cars', index)"
                            v-model="element.primary_car"
                            :readonly="!element.car"
                          />
                        </app-cells>
                      </div>
                      <button
                        @click="onRemoveRentalItem(index)"
                        class="transfer-sortable__close"
                        type="button"
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="">
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <app-cells position="center" :indent="false">
                <app-button :disabled="carsSaving || assignmentLockPending || $v.own_cars.$error || $v.rental_cars.$error" type="submit"
                            ref="submit_edit_cars">
                  Save
                </app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </app-page-container>
</template>

<script>
import {
  getCarManagementCars,
  getEmployeesListSearch,
  getReservationsSafariCars,
  getReservationSafariCars,
  getSafariRentalCompanyCarsDrivers,
  patchReservationSafariCars
} from '@/http'
import {
  prepareSafariOwnCarAssignments,
} from '@/utils/safari-assignment-locks'
import SafariAssignmentLock from '@/components/SafariAssignmentLock'
import {format, parseISO} from 'date-fns'
import {required} from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import moment from 'moment/moment'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'

const EDIT_CARS_ROLE_CODES = ['general_manager', 'safari_supervisor', 'safari_manager', 'safari_assistant']

export default {
  name: 'SafariScheduleList',
  components: {VueCtkDateTimePicker, SafariAssignmentLock},
  data() {
    return {
      is_page_loaded: false,
      is_table_loading: false,
      table: {
        items: [],
        columns: [
          'start',
          'finish',
          'days',
          'itinerary',
          'start_from',
          'finish_at',
          'type',
          'group',
          'pax',
          'ref',
          'edit_cars',
          'cars_required',
          'cars',
          'drivers'
        ],
        options: {
          headings: {
            start: 'Start',
            finish: 'Finish',
            pax: 'Pax',
            ref: 'Ref #',
            type: 'Type',
            cars_required: 'Cars',
            itinerary: 'Itinerary',
            days: 'Days',
            group: 'Group',
            edit_cars: '',
            cars: 'Cars',
            drivers: 'Drivers',
            start_from: 'Start from',
            finish_at: 'Finish at',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      own_cars: [],
      carsSaving: false,
      carsLoading: false,
      assignmentLockPending: false,
      rental_cars: [],
      edit_row: {},
      cars_options: [],
      drivers_options: [],
      company_options: [],
      search: '',
      filter_confirmed: '',
      filter_confirmed_options: [
        {value: '', label: 'All'},
        {value: 'false', label: 'Waiting confirmation'},
        {value: 'true', label: 'Confirmed'},
      ],
      date_range: null,
      custom_shortcuts: [
        {
          key: 'next7Days',
          label: 'Next 7 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(7, 'days')
            }
          }
        },
        {
          key: 'next14Days',
          label: 'Next 14 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(14, 'days')
            }
          }
        },
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
      ],
      date_picker_error: false,
    }
  },
  validations: {
    own_cars: {
      $each: {
        car: {required},
      }
    },
    rental_cars: {
      $each: {
        rental_company: {required},
        price: {required},
      }
    }
  },
  computed: {
    can_edit_cars() {
      const groups = this.$store.state.userGroups.map(item => item.code)
      return groups.some(code => EDIT_CARS_ROLE_CODES.includes(code))
    },
  },
  created() {
    Promise.all([
      getReservationsSafariCars({
        confirmed: this.search ? '' : this.filter_confirmed,
        search: this.search,
        tour_start_after: this.date_range ? this.date_range.start : null,
        tour_start_before: this.date_range ? this.date_range.end : null,
        page: this.pagination.page,
        page_size: 50
      }),
      getCarManagementCars({safari: true}),
      getEmployeesListSearch({position: 'safari_driver', employment_status: 'working'}),
      getSafariRentalCompanyCarsDrivers()
    ])
      .then(res => {
        this.table.items = res[0].data.results
        this.pagination.pages = res[0].data.pages
        this.pagination.count = res[0].data.count
        this.cars_options = res[1].data
        this.drivers_options = res[2].data
        this.company_options = res[3].data
        this.is_page_loaded = true
      })
  },
  methods: {
    onInputPicker() {
      if (!this.$refs.rage_picker.pickerOpen) this.onChangeFilter()
    },
    onIsHiddenPicker() {
      if (this.date_range?.start && this.date_range?.end) {
        this.date_picker_error = false
        this.onChangeFilter()
      } else this.date_picker_error = true
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchListItems()
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchListItems()
    },
    fetchListItems() {
      this.is_table_loading = true
      getReservationsSafariCars({
        confirmed: this.search ? '' : this.filter_confirmed,
        search: this.search,
        tour_start_after: this.date_range ? this.date_range.start : null,
        tour_start_before: this.date_range ? this.date_range.end : null,
        page: this.pagination.page,
        page_size: 50
      })
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.is_table_loading = false
        })
    },
    onCheckEditCarsForm() {
      this.$v.own_cars.$touch()
      this.$v.rental_cars.$touch()
      if (this.$v.own_cars.$invalid || this.$v.rental_cars.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else this.sendEditCarsForm()
    },
    sendEditCarsForm() {
      if (!this.can_edit_cars || this.carsSaving || this.assignmentLockPending) return
      this.carsSaving = true
      this.$refs.submit_edit_cars.preload = true
      return patchReservationSafariCars(this.edit_row.id, {
        own_cars: this.normalizeOwnCarsForm(),
        rental_cars: this.normalizeRentalCarsForm(),
      })
        .finally(() => {
          this.carsSaving = false
          this.$refs.submit_edit_cars.preload = false
        })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Cars edited successfully',
          })
          this.fetchListItems()
          this.$modal.hide('edit-cars')
        })
        .catch(() => {})
    },
    beforeCarsClose(event) {
      if (this.carsSaving || this.assignmentLockPending) event.cancel()
    },
    normalizeOwnCarsForm() {
      let own_cars = [...this.own_cars]

      if (own_cars.length) {
        own_cars = own_cars.map(item => {
          const normalized = {
            ...item,
            car: item.car.id,
            driver: item.driver ? item.driver.id : null,
          }
          delete normalized._initial_car_assignment_locked
          delete normalized._initial_guide_assignment_locked
          return normalized
        })
      } else own_cars = []

      return own_cars
    },
    normalizeRentalCarsForm() {
      let rental_cars = [...this.rental_cars]

      if (rental_cars.length) {
        rental_cars = rental_cars.map(item => {
          return {
            ...item,
            rental_company: item.rental_company.id,
            driver: item.driver ? item.driver.id : null,
            car: item.car.id,
          }
        })
      } else rental_cars = []

      return rental_cars
    },
    onRemoveRentalItem(index) {
      this.rental_cars.splice(index, 1)
    },
    onAddRental() {
      this.rental_cars.push({
        rental_company: null,
        car: null,
        driver: null
      })
    },
    onRemoveOwnItem(index) {
      if (this.carsSaving || this.assignmentLockPending) return
      const assignment = this.own_cars[index]
      if (
        assignment.car_assignment_locked ||
        assignment.guide_assignment_locked ||
        assignment._initial_car_assignment_locked ||
        assignment._initial_guide_assignment_locked
      ) return
      this.own_cars.splice(index, 1)
    },
    onAddOwnCar() {
      this.own_cars.push({
        car: null,
        driver: null,
        car_assignment_locked: false,
        guide_assignment_locked: false,
        _initial_car_assignment_locked: false,
        _initial_guide_assignment_locked: false,
      })
    },
    onRentalCarSelected(car, index) {
      this.rental_cars[index].driver = null
      if (car.driver && car.driver.length && car.driver.length === 1) {
        this.rental_cars[index].driver = car.driver[0]
      }
    },
    onRentalCompanySelected(index) {
      this.rental_cars[index].car = null
      this.rental_cars[index].driver = null
    },
    onPrimarySwitcherChanged(event, type, index) {
      if (event && (this.own_cars.some(item => item.primary_car) || this.rental_cars.some(item => item.primary_car))) {
        [...this.own_cars, ...this.rental_cars].forEach(el => {
          this.$set(el, 'primary_car', false)
        })
        this[type][index].primary_car = true
      }
    },
    onOwnCarSelected(car, index) {
      if (this.own_cars[index].guide_assignment_locked) return
      this.own_cars[index].driver = null
      if (car.driver) this.own_cars[index].driver = car.driver
      else this.own_cars[index].driver = null
    },
    async onEditCars(row) {
      if (!this.can_edit_cars || this.carsLoading || this.carsSaving || this.assignmentLockPending) return
      this.carsLoading = true
      let data = null
      try {
        const response = await getReservationSafariCars(row.id)
        data = response.data
      } catch (error) {
        return
      } finally {
        this.carsLoading = false
      }
      this.own_cars = this.prepareOwnCars(data.own_cars)
      this.rental_cars = structuredClone(data.rental_cars)
      this.edit_row = {
        id: row.id,
        tour_start: row.tour_start,
        tour_end: row.tour_end,
        cars_required: row.cars_required,
        lead_name: row.lead.name,
        pax: row.pax,
        days: row.days,
        short_itinerary: row.short_itinerary,
      }
      this.$modal.show('edit-cars')
    },
    prepareOwnCars(assignments) {
      return prepareSafariOwnCarAssignments(structuredClone(assignments))
    },
    dateFormat(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM yyyy')
    },
    dateShortFormat(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM')
    },
    onListSearch() {
      this.searchList(this)
    },
    searchList: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchListItems()
    }, 350),
  }
}
</script>
